import type { MouseEvent } from 'react';
import { PhinAvatarLazy } from '@belong/ai-pointer';
import { Text } from '@belong/ui';
import {
  MaintenanceModel,
  MaintenancePaymentModel,
  MaintenancePriceRangeAggregateModel,
} from 'api/models';
import clsx from 'clsx';
import { ICONS } from 'components/Icon/Icon.js';
import { MAINTENANCE_TASK_MOBILE_PROPS } from 'containercomponents/MaintenanceTaskListContainerComponent/MaintenanceTasks/MaintenanceProps';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { ICON_SLOT_CLASS_NAME } from 'maintenance/constants';
import { useMaintenanceLabels } from 'maintenance/hooks/useMaintenanceLabels';
import { needsWorkCompletionPrompt } from 'maintenance/utils/appointments';
import { splitCamelCase } from 'utils/strings';
import MaintenanceTaskCost from './MaintenanceTaskCost/MaintenanceTaskCost';
import MaintenanceTaskIconLabel from './MaintenanceTaskIconLabel/MaintenanceTaskIconLabel';
import MaintenanceTaskLabels from './MaintenanceTaskLabels/MaintenanceTaskLabels';
import { WorkCompletionPrompt } from 'containercomponents/MaintenanceStatus/MaintenanceScheduleComponent/WorkCompletionPrompt/work-completion-prompt';

type MaintenanceTasksProps = {
  maintenanceDate?: string;
  maintenanceTaskImage?: string;
  maintenanceTask?: string;
  // `undefined` is included because the caller leaves both unassigned on the
  // paths that compute neither, and `MaintenanceTaskCost` already tests for it.
  estimatedCost:
    | ''
    | 'Pricing TBD'
    | MaintenancePaymentModel['estimatedAdjustmentApplied']
    | undefined;
  actualCost:
    | ''
    | 'Free'
    | MaintenancePaymentModel['actualAdjustmentApplied']
    | undefined;
  maintenanceTaskStatus?: string;
  isConsentPendingList?: boolean;
  priceRange?: MaintenancePriceRangeAggregateModel;
  numberOfOptions?: number;
  requestCategory?: MaintenanceModel['requestCategory'];
  requestType?: MaintenanceModel['requestType'];
  isRenter: boolean;
  task: MaintenanceModel;
  onOutcomeClick?: (appointmentId: string) => void;
};

const MaintenanceTasks = ({
  maintenanceDate,
  maintenanceTaskImage = ICONS.COMMUNICATION.DEFAULT,
  maintenanceTask = 'Please Enter A Task',
  estimatedCost,
  actualCost,
  priceRange,
  numberOfOptions,
  maintenanceTaskStatus,
  isConsentPendingList = false,
  requestCategory,
  requestType,
  isRenter,
  task,
  onOutcomeClick,
}: MaintenanceTasksProps) => {
  const maintenanceLabels = useMaintenanceLabels(isRenter, task);

  const formattedRequestType = task.requestType
    ? splitCamelCase(task.requestType)
    : task.requestType;
  const maintenanceAssessment = task.assessment
    ? `${task.assessment} ${formattedRequestType}`
    : formattedRequestType;

  const { isEnabled: isWorkCompletionEnabled } = useFeatureFlag(
    'work-completion-consumer',
  );

  const pendingOutcomeAppointment = task.appointments?.find(
    needsWorkCompletionPrompt,
  );

  const showOutcomeFlow =
    isWorkCompletionEnabled &&
    !!pendingOutcomeAppointment &&
    onOutcomeClick &&
    isRenter;

  const handleOutcomeClick = (event: MouseEvent<HTMLButtonElement>) => {
    // The prompt is nested inside the card's ButtonBase; stop the click from
    // bubbling to the card handler, which would navigate without the param.
    event.stopPropagation();

    if (pendingOutcomeAppointment) {
      onOutcomeClick?.(pendingOutcomeAppointment.uniqueId);
    }
  };

  return (
    <>
      <div
        className={clsx(
          'min-h-[86px] rounded border border-solid border-gray transition-colors duration-hover hover:border-navy hover:bg-light-gray',
          showOutcomeFlow && 'rounded-b-none border-b-0',
          isConsentPendingList && 'border-red bg-red-translucent-light',
        )}
      >
        <div className="w-full p-sm">
          <div className="flex flex-col md:flex-row md:justify-between">
            <MaintenanceTaskIconLabel
              maintenanceTaskImage={maintenanceTaskImage}
              maintenanceTask={maintenanceTask}
              maintenanceDate={maintenanceDate!}
              maintenanceAssessment={maintenanceAssessment}
            />
            <MaintenanceTaskLabels maintenanceLabels={maintenanceLabels} />
          </div>
          <div className="mt-sm flex flex-wrap border-0 border-t border-dashed border-gray pt-sm">
            <div className="w-full md:w-1/2">
              <MaintenanceTaskCost
                maintenanceTaskStatus={maintenanceTaskStatus}
                estimatedCost={estimatedCost}
                actualCost={actualCost}
                priceRange={priceRange}
                numberOfOptions={numberOfOptions}
                requestCategory={requestCategory}
                requestType={requestType}
                mobileDisplay={MAINTENANCE_TASK_MOBILE_PROPS.HAS_MOBILE}
              />
            </div>
            {task.agentConversationThreadId && (
              <div className="flex w-full items-center justify-end gap-2xs md:w-1/2">
                <div className={ICON_SLOT_CLASS_NAME}>
                  <PhinAvatarLazy
                    size={24}
                    paused
                    interactive={false}
                    idleJumps={false}
                  />
                </div>
                <Text variant="p1">
                  <b>Phin</b> Created Ticket
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
      {showOutcomeFlow && (
        <WorkCompletionPrompt
          customClassName="rounded-t-none"
          scheduledStartOn={pendingOutcomeAppointment?.scheduledStartOn}
          scheduledEndOn={pendingOutcomeAppointment?.scheduledEndOn}
          timeZone={task.home?.timeZone}
          onClick={handleOutcomeClick}
        />
      )}
    </>
  );
};

export default MaintenanceTasks;
